import { useState } from 'react';
import { Link } from '@tanstack/react-router';

import type { WorkspaceTaskRow } from '@/features/tasks';
import { SECTION_HEADING } from '@/shared/components/sectionHeadingClass';
import { finishedSentence, finishedWeek } from '../finishedWeek';

/**
 * Finished this week (spec 155 US3): the one sign of momentum on a page that
 * otherwise shows only what is wrong, waiting or running.
 *
 * Seven bars, one per local day, oldest first, in the done colour. The bars
 * are a picture of the sentence beside them; assistive technology reads the
 * sentence. The total opens the Tasks page's "Completed, last 7 days", which
 * lists exactly that many (FR-010).
 */
export function FinishedThisWeek({
  workspaceId,
  rows,
}: {
  workspaceId: string;
  rows: readonly WorkspaceTaskRow[];
}) {
  // The week as of when the page opened: its days do not shift under a
  // reader, and a reload brings them up to date.
  const [now] = useState(() => Date.now());
  const week = finishedWeek(rows, now);
  const sentence = finishedSentence(week, now);
  const busiest = Math.max(1, ...week.days.map((day) => day.count));
  const initial = new Intl.DateTimeFormat(undefined, { weekday: 'narrow' });

  return (
    <section
      aria-labelledby="overview-finished"
      className="flex flex-col gap-2.5"
    >
      <h2 id="overview-finished" className={SECTION_HEADING}>
        Finished this week
      </h2>
      <div className="border-border bg-card flex flex-col gap-2.5 rounded-lg border px-4 py-3.5">
        {week.total === 0 ? (
          <p className="text-muted-foreground text-xs">{sentence}</p>
        ) : (
          <>
            <div aria-hidden="true" className="flex h-12 items-end gap-1.5">
              {week.days.map((day) => (
                <div
                  key={day.start}
                  className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
                >
                  <span
                    className={`w-full rounded-sm ${
                      day.count > 0 ? 'bg-board-state-done' : 'bg-muted'
                    }`}
                    style={{
                      height:
                        day.count > 0
                          ? `${String(Math.max(12, (day.count / busiest) * 100))}%`
                          : '2px',
                    }}
                  />
                  <span className="text-muted-foreground text-2xs leading-none">
                    {initial.format(day.start)}
                  </span>
                </div>
              ))}
            </div>
            <p className="sr-only">{sentence}</p>
            <Link
              to="/workspaces/$workspaceId/tasks"
              params={{ workspaceId }}
              search={{ state: 'completed', since: '7d', on: 'finished' }}
              className="text-foreground touch-pad self-start text-xs underline-offset-4 hover:underline"
            >
              {`${String(week.total)} finished this week`}
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
